<template>
    <mini-calculator-page>
        <div slot="summary-panel">
            <div class="p-animated-calc__results a-form-inputs u-full-width u-margin-auto">
                <mini-calculator-header title="Pre and Post Assessment Discounts" icon="table-e-f-discounts"/>

                <control for="pre_or_pos" label="Discount Type">
                    <select v-field-control
                            required
                            name="pre_or_pos"
                            v-model="preOrPost" ref="preOrPost"
                            
                            @change="handler">
                        <option value="null" disabled hidden>Please select...</option>
                        <option value="pre">Pre-Assessment Discount</option>
                        <option value="post">Post-Assessment Discount</option>
                    </select>
                    <control-error :show="$error('pre_or_pos')">
                        Please select Pre or Post
                    </control-error>
                </control>

                <control for="gender" label="Gender">
                    <select v-field-control
                            required
                            name="gender"
                            v-model="gender" ref="gender"
                            
                            @change="handler">
                        <option value="null" disabled hidden>Please select...</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                    </select>
                    <control-error :show="$error('gender')">
                        The gender is required
                    </control-error>
                </control>

                <control for="date_of_birth" label="Date of Birth">
                    <date-input v-model="dateOfBirth" ref="dateOfBirth"
                                                        v-field-control:date_of_birth.required="{type:'date'}"
                                @input="handler">

                    </date-input>
                    <control-error :show="$error('date_of_death')">
                        The Date of Death is required and must be in the format DD/MM/YYYY
                        and The Age of Deceased has to be between 10 - 90
                    </control-error>
                </control>

                <control for="age_of_deceased" label="Age of Deceased" :no-flex-margin="true" :vertical-centre="true">
                    <input autocomplete="off" name="age_of_deceased"
                                                   v-model="ageOfDeceased" ref="ageOfDeceased"
                           v-field-control
                           required
                           min="10"
                           max="90"
                           type="number"
                           @input="handler">
                    <control-error :show="$error('age_of_deceased') || !ageRules">
                        The Age of Deceased has to be between 10 - 90
                    </control-error>
                </control>

                <span class="u-accent u-r-margin-top-20 u-d-block">or</span>

                <control for="age_of_deceased" label="Date of Death" :vertical-centre="true">
                    <date-input v-model="dateOfDeath" ref="dateOfDeath"
                                v-field-control:dateOfDeath.required="{type:'date'}"
                                @input="handler">
                    </date-input>
                </control>

                <control for="date_of_trial_or_cessation" label="Date of Trial">
                    <date-input v-model="dateOfTrialOrCessation" ref="dateOfTrialOrCessation"
                                v-field-control:date_of_trial_or_cessation.required="{type:'date'}"
                                                            @input="handler">

                    </date-input>
                    <control-error :show="$error('date_of_trial_or_cessation') || !dateOfTrialOrCessationRules">
                        The Date of Trial is required and must be in the format DD/MM/YYYY
                        and can not be more than 9 years after the Date of Death
                    </control-error>
                </control>

                <h2 class="u-r-margin-top-40">
                    Result
                </h2>

                <control for="discount_result" :label="discountResultLabel">
                    <input autocomplete="off" disabled="disabled"
                           id="discount_result" type="number"
                           name="discount_result"
                           v-two-dp
                           v-model="discountResult">
                </control>
            </div>
        </div>

        <div slot="results-panel" v-show="showResults">
            <div class="p-animated-calc__results-panel u-full-width u-margin-auto">
                <h2>
                    Explanation
                </h2>

                <p>
                    To calculate the factor by which pre or post-trial damages should be discounted, Ogden Tables E and F
                    are used, respectively.
                </p>

                <p>
                    Using Ogden Table {{ preOrPost == 'pre' ? "E" : "F" }} the factor by which
                    {{ preOrPost }}-trial damages should be discounted can be calculated using the age of the deceased at
                    the date of death and the number of years between the date of death and the
                    date of trial/assumed settlement.
                </p>

                <div class="a-table--really-wide" v-if="tableData !== null">
                    <table class="a-table a-table--modal a-table--really-wide">
                        <thead>
                        <tr>
                            <th colspan="6">
                                <div class="u-text-center">
                                    Period from date of accident to date of trial or assumed settlement
                                </div>
                            </th>
                        </tr>
                        <tr>
                            <th v-for="column in tableData.table.thead">
                                {{ column }}
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="row in tableData.table.tbody">
                            <td v-for="(column, index) in row">
                                {{ index === 0 || index === 5 ? column : currencyFormat(column, '') }}
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                <br>

                <h2>
                    Calculation
                </h2>

                <p>
                    The age of the deceased at the date of the accident was
                    {{ ageOfDeceased }} years old and the period from the date of the accident to the date of
                    trial or assumed settlement of
                    dependency was {{ periodBetweenDodAndDot }} years.
                </p>

                <p v-if="explanation !== null">
                    {{ explanation }}
                </p>
                <p>
                    Through interpolation it has been deduced that the
                    {{ preOrPost }}-assessment discount is: <strong>{{ currencyFormat(discountResult, '') }}</strong>
                </p>
            </div>
        </div>
    </mini-calculator-page>
</template>

<script type="text/babel">
    import {mapGetters, mapActions} from 'vuex';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import MiniCalculatorPage from 'pages/mini-calculators/components/MiniCalculatorPage';
    import miniCalcIcons from 'components/Icons.vue';
    import DateInput from 'components/form/control/DateInput.vue';
    import CalculatorKeyBindings from 'pages/mini-calculators/calculators/mixins/MiniCalculatorBindings';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import Intro from 'pages/mini-calculators/calculators/CalculatorComponents/Intro';
    import moment from 'moment';
    import currencyFormat from 'mixins/currencyFormat';
    import MiniCalculatorHeader from 'pages/mini-calculators/components/MiniCalculatorHeader';

    export default {
        name: 'table-discounts',
        created() {
            this.resetForm();
        },
        data: function() {
            return {
                showResults: false
            }
        },
        computed: {
            ...mapGetters('mini/tableDiscounts', {
                getValue: 'value'
            }),
            preOrPost: {
                get() {
                    return this.getValue('preOrPost');
                },
                set(value) {
                    this.updateValue({key: 'preOrPost', value});
                }
            },
            gender: {
                get() {
                    return this.getValue('gender');
                },
                set(value) {
                    this.updateValue({key: 'gender', value});
                }
            },
            ageOfDeceased: {
                get() {
                    return this.getValue('ageOfDeceased');
                },
                set(value) {
                    this.updateValue({key: 'ageOfDeceased', value});
                    this.calcDod();
                }
            },
            dateOfBirth: {
                get() {
                    return this.getValue('dateOfBirth');
                },
                set(value) {
                    this.updateValue({key: 'dateOfBirth', value});
                    this.calcAge();
                }
            },
            dateOfDeath: {
                get() {
                    return this.getValue('dateOfDeath');
                },
                set(value) {
                    this.updateValue({key: 'dateOfDeath', value});
                    this.calcAge();
                }
            },
            dateOfTrialOrCessation: {
                get() {
                    return this.getValue('dateOfTrialOrCessation');
                },
                set(value) {
                    this.updateValue({key: 'dateOfTrialOrCessation', value});
                }
            },
            discountResult: {
                get() {
                    return this.getValue('discountResult');
                },
            },
            discountResultLabel: {
                get() {
                    return this.getValue('label');
                },
            },
            tableData: {
                get() {
                    return this.getValue('tableData');
                }
            },
            explanation: {
                get() {
                    return this.getValue('explanation');
                }
            },
            periodBetweenDodAndDot() {
                return moment(this.dateOfTrialOrCessation, 'DD/MM/YYYY').diff(moment(this.dateOfDeath, 'DD/MM/YYYY'), 'years');
            },
            ageRules() {
                return this.ageOfDeceased ? (this.ageOfDeceased >= 10 && this.ageOfDeceased <= 90 ) : true;
            },
            dateOfTrialOrCessationRules() {
                return this.dateOfTrialOrCessation ? (this.periodBetweenDodAndDot >= 0 && this.periodBetweenDodAndDot <= 9) : true;
            }
        },
        mixins: [
            FormValidator,
            CalculatorKeyBindings,
            currencyFormat
        ],
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        components: {
            Control,
            ControlError,
            DateInput,
            MiniCalculatorPage,
            MiniCalculatorHeader,
            miniCalcIcons,
            Intro,
        },
        methods: {
            ...mapActions('mini/tableDiscounts', {
                resetForm: 'reset',
                updateValue: 'update',
                calculateMultiplier: 'calcMultiplier'
            }),
            resetTheForm() {
                this.resetForm()
            },
            calcAge() {
                if (this.dateOfBirth && this.dateOfDeath) {
                    let dateOfDeath = moment(this.dateOfDeath, 'DD/MM/YYYY');
                    let dateOfBirth = moment(this.dateOfBirth, 'DD/MM/YYYY');
                    let ageOfDeceased = dateOfDeath.diff(dateOfBirth, 'years');
                    this.updateValue({key: 'ageOfDeceased', value: ageOfDeceased});
                }
            },
            calcDod() {
                if (this.dateOfBirth && this.ageOfDeceased) {
                    let dateOfBirth = moment(this.dateOfBirth, 'DD/MM/YYYY');
                    let ageOfDeceased = this.ageOfDeceased;
                    let newDateOfBirth = dateOfBirth.add(ageOfDeceased, 'years').format('DD/MM/YYYY');
                    this.updateValue({key: 'dateOfDeath', value: newDateOfBirth});
                }
            },
            isValid() {
                let diffInYears = moment(this.dateOfTrialOrCessation, 'DD/MM/YYYY').diff(moment(this.dateOfDeath, 'DD/MM/YYYY'), 'years');

                return (
                    this.preOrPost !== null
                    && this.gender !== null
                    && this.dateOfBirth !== null
                    && moment(this.dateOfBirth, 'DD/MM/YYYY').isValid()
                    && this.ageOfDeceased >= 10
                    && this.ageOfDeceased <= 90
                    && this.dateOfTrialOrCessation !== null
                    && moment(this.dateOfTrialOrCessation, 'DD/MM/YYYY').isValid()
                    && diffInYears >= 0 && diffInYears <= 9
                );
            },
            calcMultiplier() {
                if (!this.isValid()) {
                    return;
                }

                this.calculateMultiplier();
            },
            handler: function(){
                this.calcMultiplier();

                if (!this.isValid()) {
                    return;
                }

                this.hidePrompt();
                this.showResults = true;
            }
        }
    }
</script>



// WEBPACK FOOTER //
// tableDiscounts.vue?76249a2c